'use client';
import * as React from 'react';
import { Waveform } from '@core/ui/editors/Waveform';
import { Button } from '@/core/ui/primitives/button';
import { Spinner } from '@/core/ui/primitives/spinner';
import type { TranscriptionState } from '@audio-transcription/core/AudioTranscriptionTypes';
type Feedback = {
  type: 'success' | 'error';
  text: string;
} | null;
type OverlayProps = {
  state: TranscriptionState;
  seconds: number;
  analyser: AnalyserNode | null;
  dataArray: Uint8Array | null;
  bufferLength: number;
  onCancel: () => void;
  onAccept: () => void;
  feedback: Feedback;
  onDismissFeedback: () => void;
};
export function AudioTranscriptionOverlay({
  state,
  seconds,
  analyser,
  dataArray,
  bufferLength,
  onCancel,
  onAccept,
  feedback,
  onDismissFeedback,
}: OverlayProps) {
  const [isMounted, setIsMounted] = React.useState(false);
  React.useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);
  const animatedState = isMounted ? 'opacity-100 scale-100' : 'opacity-0 scale-95';
  return (
    <div className='pointer-events-none fixed inset-x-0 bottom-6 z-[1100] flex flex-col items-center gap-2'>
      {state !== 'idle' && (
        <div
          className={`pointer-events-auto w-[200px] rounded-full bg-white/95 px-2 py-1 shadow-lg backdrop-blur-sm transition-all duration-150 ease-out dark:bg-gray-900/90 dark:shadow-black/40 ${animatedState}`}>
          <div className='flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-300'>
            {renderBody(state, seconds, analyser, dataArray, bufferLength, onCancel, onAccept)}
          </div>
        </div>
      )}
      {feedback ? (
        <div
          className={`pointer-events-auto flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium shadow ${
            feedback.type === 'success'
              ? 'bg-emerald-500/90 text-white shadow-emerald-500/30'
              : 'bg-red-500/90 text-white shadow-red-500/30'
          }`}>
          <svg className='h-3.5 w-3.5 flex-shrink-0' aria-hidden='true' focusable='false'>
            <use
              href={feedback.type === 'success' ? '/app-icons.svg#fas-check' : '/app-icons.svg#fas-circle-exclamation'}
            />
          </svg>
          <span className='truncate'>{feedback.text}</span>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className='ml-1 rounded-full p-0.5 text-white/80 transition hover:bg-white/20'
            onClick={onDismissFeedback}>
            <svg className='h-3 w-3' aria-hidden='true' focusable='false'>
              <use href='/app-icons.svg#fas-xmark' />
            </svg>
          </Button>
        </div>
      ) : null}
    </div>
  );
}
function renderBody(
  state: TranscriptionState,
  seconds: number,
  analyser: AnalyserNode | null,
  dataArray: Uint8Array | null,
  bufferLength: number,
  onCancel: () => void,
  onAccept: () => void
) {
  if (state === 'processing') {
    return (
      <div className='flex w-full items-center justify-center gap-2 text-gray-600 dark:text-gray-300'>
        <Spinner className='size-3 text-gray-500 dark:text-gray-300 flex-shrink-0' />
        <span className='truncate'>Transcribing…</span>
      </div>
    );
  }
  if (state === 'recording') {
    return (
      <>
        <Button
          type='button'
          variant='ghost'
          size='icon'
          onClick={onCancel}
          className='flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 transition hover:border-red-200 hover:text-red-600 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-400 dark:hover:border-red-500 dark:hover:text-red-400'
          title='Cancel recording (Esc)'>
          <svg className='h-3.5 w-3.5' aria-hidden='true' focusable='false'>
            <use href='/app-icons.svg#fas-xmark' />
          </svg>
        </Button>
        <div className='flex-1 overflow-hidden'>
          <Waveform
            analyser={analyser}
            dataArray={dataArray}
            bufferLength={bufferLength}
            isRecording={state === 'recording'}
          />
        </div>
        <div className='font-mono text-xs tabular-nums text-gray-700 dark:text-gray-200'>{formatTime(seconds)}</div>
        <Button
          type='button'
          variant='ghost'
          size='icon'
          onClick={onAccept}
          className='flex h-8 w-8 items-center justify-center rounded-full border border-emerald-200 bg-emerald-500 text-white transition hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-400/60 focus:ring-offset-2 dark:border-emerald-700 dark:bg-emerald-600 dark:focus:ring-offset-gray-900'
          title='Finish and transcribe (⌘/Ctrl+⌥R)'>
          <svg className='h-3.5 w-3.5' aria-hidden='true' focusable='false'>
            <use href='/app-icons.svg#fas-check' />
          </svg>
        </Button>
      </>
    );
  }
  return (
    <div className='flex w-full items-center justify-center gap-2 text-gray-600 dark:text-gray-300'>
      <svg className='h-3.5 w-3.5 flex-shrink-0' aria-hidden='true' focusable='false'>
        <use href='/app-icons.svg#fas-microphone' />
      </svg>
      <span className='truncate'>Ready to transcribe</span>
    </div>
  );
}
function formatTime(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, '0');
  const seconds = (totalSeconds % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
}
